import React, { useState } from 'react';
import { Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { orphanFormazioneApi } from '../lib/api';

/**
 * Bottone + dialog per la pulizia dei record ``formazione`` orfani
 * (workerId mancante o non più esistente). Preview → conferma → backup
 * automatico in ``formazione_archive_orphans_YYYYMMDDHHMMSS`` e delete.
 */
const OrphanCleanupButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    setResult(null);
    setLoading(true);
    try {
      const { data } = await orphanFormazioneApi.preview();
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview orfani');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const doApply = async () => {
    if (!window.confirm(`Confermi la pulizia di ${preview?.totale || 0} record orfani? Verranno archiviati in una collection di backup.`)) return;
    setApplying(true);
    try {
      const { data } = await orphanFormazioneApi.apply();
      setResult(data);
      toast.success(`${data.deleted} record archiviati in ${data.backup_collection}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore pulizia');
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        data-testid="orphan-cleanup-btn"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-red-200 text-red-700 hover:bg-red-50"
        title="Pulizia record formazione senza lavoratore"
      >
        <Trash2 size={13} /> Pulisci orfani
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="orphan-cleanup-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[min(96vw,640px)] max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-red-800 flex items-center gap-2">
                  <Trash2 size={18} /> Pulizia record formazione orfani
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Record con <code>workerId</code> mancante o riferito ad un lavoratore che non esiste più.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100">
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {loading && (
                <div className="text-center py-8 text-slate-500 text-sm">
                  <Loader2 size={20} className="inline animate-spin mr-2" /> Analisi in corso…
                </div>
              )}
              {!loading && preview && (
                <>
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Totale orfani</div>
                    <div className="text-3xl font-extrabold text-red-800 mt-1">{preview.totale}</div>
                  </div>

                  {preview.reasons && preview.totale > 0 && (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[
                        ['workerId_mancante', 'workerId mancante'],
                        ['worker_inesistente', 'lavoratore inesistente'],
                        ['worker_senza_nome', 'lavoratore senza nome'],
                        ['worker_archiviato_merge', 'archiviato da merge (residuo)'],
                      ].map(([k, lbl]) => (
                        <div key={k} className="rounded-lg border border-slate-200 px-3 py-2 bg-slate-50">
                          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{lbl}</div>
                          <div className="text-lg font-bold text-slate-800 mt-0.5">{preview.reasons[k] || 0}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {preview.totale > 0 && (
                    <>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Distribuzione per azienda (top 10)</div>
                        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm max-h-40 overflow-y-auto">
                          {Object.entries(preview.by_company || {})
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 10)
                            .map(([cid, n]) => (
                              <div key={cid} className="flex items-center justify-between px-3 py-1.5">
                                <span className="font-mono text-xs text-slate-600">{cid}</span>
                                <span className="text-xs font-bold text-red-700">{n}</span>
                              </div>
                            ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Campione (primi 20)</div>
                        <div className="rounded-lg border border-slate-200 max-h-48 overflow-y-auto text-[11px] font-mono">
                          {(preview.sample || []).map((r, i) => (
                            <div key={i} className="px-3 py-1 border-b border-slate-100 last:border-b-0 text-slate-600">
                              {r.id} · <span className="text-slate-400">{r.categoria || '—'}</span> · {r.corso || '—'} · {r.dataRilascio || '—'}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {result && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Pulizia completata</div>
                  <div className="text-sm text-emerald-900 mt-1">
                    Archiviati <strong>{result.archived}</strong> record · Eliminati <strong>{result.deleted}</strong>
                  </div>
                  {result.backup_collection && (
                    <div className="text-[11px] font-mono text-emerald-800 mt-1">
                      Backup: {result.backup_collection}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
                data-testid="orphan-cleanup-close"
              >
                Chiudi
              </button>
              {preview && preview.totale > 0 && !result && (
                <button
                  onClick={doApply}
                  disabled={applying}
                  data-testid="orphan-cleanup-apply"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white disabled:opacity-60 flex items-center gap-2"
                >
                  {applying ? <><Loader2 size={14} className="animate-spin" /> Pulisco…</> : <><Trash2 size={14} /> Archivia e pulisci</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrphanCleanupButton;
